import { appState } from "./state";
import { notifyStateChange } from "./stateBus";
import { apiFetch } from "./api";
import { requestLock } from "./lockBus";
import type { Gift } from "./types";

// Framework-agnostic gifts data layer - React-only feature (built after the
// full migration, Phase 10+), so unlike chatApi.ts/galleryApi.ts there's no
// vanilla consumer to worry about, but appState/notifyStateChange are used
// anyway for consistency with every other list in this app.

export async function loadGiftsData(): Promise<void> {
  try {
    const res = await apiFetch("/api/gifts");
    if (res.status === 401) {
      requestLock("unauthorized");
      return;
    }
    if (!res.ok) return;
    const data = await res.json();
    appState.gifts = Array.isArray(data.gifts) ? data.gifts : [];
    notifyStateChange();
  } catch {
    // Best effort - the next protected-features refresh tick retries.
  } finally {
    appState.giftsLoaded = true;
    notifyStateChange();
  }
}

export function unreadGiftCount(gifts: Gift[]): number {
  return gifts.filter((g) => g.direction === "received" && !g.viewedAt).length;
}

export type SendGiftResult = { ok: true; gift: Gift } | { ok: false; error?: string };

export async function sendGiftApi(params: {
  recipientEmail: string;
  mode: "once" | "keep";
  file: File;
  unlockAt?: string | null;
  message?: string;
  openTogether?: boolean;
}): Promise<SendGiftResult> {
  const fd = new FormData();
  fd.append("attachment", params.file);
  fd.append("recipientEmail", params.recipientEmail);
  fd.append("mode", params.mode);
  if (params.unlockAt) fd.append("unlockAt", params.unlockAt);
  if (params.message) fd.append("message", params.message);
  if (params.openTogether) fd.append("openTogether", "true");

  const res = await apiFetch("/api/gifts", { method: "POST", body: fd });

  if (res.status === 401) {
    requestLock("unauthorized");
    return { ok: false };
  }
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    return { ok: false, error: data.message };
  }

  const data = await res.json();
  appState.gifts = [data.gift, ...appState.gifts];
  notifyStateChange();
  return { ok: true, gift: data.gift };
}

export type SendVirtualGiftResult = { ok: true; gift: Gift; balance: number } | { ok: false; error?: string; balance?: number; price?: number };

// Sends a catalog (virtual) gift - see server.js's POST /api/gifts/virtual.
// Only `giftId` is sent; price/name/emoji are resolved server-side from the
// authoritative catalog, never trusted from the client (PART 6). Updates
// appState.credits from the response's own balance (never computed
// locally) alongside the same unshift-into-appState.gifts pattern every
// other send* function here already uses.
export async function sendVirtualGiftApi(params: { giftId: string; recipientEmail: string; message?: string }): Promise<SendVirtualGiftResult> {
  const res = await apiFetch("/api/gifts/virtual", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(params),
  });

  if (res.status === 401) {
    requestLock("unauthorized");
    return { ok: false };
  }
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    return { ok: false, error: data.message, balance: data.balance, price: data.price };
  }

  const data = await res.json();
  appState.gifts = [data.gift, ...appState.gifts];
  appState.credits = data.balance;
  notifyStateChange();
  return { ok: true, gift: data.gift, balance: data.balance };
}

// Gifts an item already in the gallery instead of uploading a fresh
// duplicate - see server.js's POST /api/gifts/from-existing. Same
// recipient/mode/unlockAt rules and same appState update as sendGiftApi,
// just no FormData/file involved.
export async function sendGiftFromExistingApi(params: {
  filename: string;
  recipientEmail: string;
  mode: "once" | "keep";
  unlockAt?: string | null;
  message?: string;
  openTogether?: boolean;
}): Promise<SendGiftResult> {
  const res = await apiFetch("/api/gifts/from-existing", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(params),
  });

  if (res.status === 401) {
    requestLock("unauthorized");
    return { ok: false };
  }
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    return { ok: false, error: data.message };
  }

  const data = await res.json();
  appState.gifts = [data.gift, ...appState.gifts];
  notifyStateChange();
  return { ok: true, gift: data.gift };
}

export async function markGiftViewedApi(giftId: string): Promise<Gift | null> {
  const res = await apiFetch(`/api/gifts/${giftId}/view`, { method: "POST" });

  if (res.status === 401) {
    requestLock("unauthorized");
    return null;
  }
  if (!res.ok) return null;

  const data = await res.json();
  appState.gifts = appState.gifts.map((g) => (g.id === giftId ? data.gift : g));
  notifyStateChange();
  return data.gift as Gift;
}

// Pushed via the gift_unlocked WS event (chatSocket.ts) when the server's
// unlock sweep flips a gift's lock state - same full-replace-by-id shape as
// every other mutation in this file, so the row just re-renders normally.
export function applyGiftUnlocked(gift: Gift): void {
  appState.gifts = appState.gifts.map((g) => (g.id === gift.id ? gift : g));
  notifyStateChange();
}

// Pushed via the gift_received WS event when someone else sends *this*
// user a gift - mirrors sendGiftApi's own local unshift on the sender's
// side, so the recipient sees it appear the same way without waiting on
// the next full loadGiftsData() refetch.
export function applyGiftReceived(gift: Gift): void {
  if (appState.gifts.some((g) => g.id === gift.id)) return;
  appState.gifts = [gift, ...appState.gifts];
  notifyStateChange();
}

export type SaveGiftResult = { ok: true; gift: Gift } | { ok: false; error?: string };

// The recipient's choice, made after receiving (Snapchat/WhatsApp-status
// inspired) - not something the sender picks at send time. Only valid for
// "keep" gifts that haven't already been saved.
export async function saveGiftToAlbumApi(giftId: string, albumId: string): Promise<SaveGiftResult> {
  const res = await apiFetch(`/api/gifts/${giftId}/add-to-album`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ albumId }),
  });

  if (res.status === 401) {
    requestLock("unauthorized");
    return { ok: false };
  }
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    return { ok: false, error: data.message };
  }

  const data = await res.json();
  appState.gifts = appState.gifts.map((g) => (g.id === giftId ? data.gift : g));
  notifyStateChange();
  return { ok: true, gift: data.gift };
}

export type ReactToGiftResult = { ok: true; reactions: Gift["reactions"] } | { ok: false; error?: string };

// Reacting to an opened Private Drop - one reaction per user, replaces
// (not adds to) a prior one from the same user, mirrors the server's own
// GiftsDB.setReaction semantics exactly. Pass emoji=null to clear yours.
export async function reactToGiftApi(giftId: string, emoji: string | null): Promise<ReactToGiftResult> {
  const res = await apiFetch(`/api/gifts/${giftId}/react`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ emoji }),
  });

  if (res.status === 401) {
    requestLock("unauthorized");
    return { ok: false };
  }
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    return { ok: false, error: data.message };
  }

  const data = await res.json();
  appState.gifts = appState.gifts.map((g) => (g.id === giftId ? { ...g, reactions: data.reactions } : g));
  notifyStateChange();
  return { ok: true, reactions: data.reactions };
}

// Pushed via the gift_reaction WS event (chatSocket.ts) to the SENDER when
// the recipient reacts to an opened drop - same in-place patch pattern as
// applyGiftUnlocked, so their own Sent view reflects it live.
export function applyGiftReaction(data: { giftId: string; reactions: Gift["reactions"] }): void {
  appState.gifts = appState.gifts.map((g) => (g.id === data.giftId ? { ...g, reactions: data.reactions } : g));
  notifyStateChange();
}

// Private Drops V2: "open the reveal dialog for this gift", callable from
// anywhere (a chat card tap, the Open Together waiting room's own
// synchronized start, the list card) - see state.ts's openingGiftId comment
// for why this indirection exists (GiftViewerDialog's mount state lives
// inside GiftsScreen.tsx, which a WS event arriving on a different screen
// can't reach directly).
export function openGiftViewer(giftId: string): void {
  appState.openingGiftId = giftId;
  notifyStateChange();
}

export type OpenTogetherStatus = { waiting: boolean; partnerJoined: boolean; started: boolean; partnerOnline: boolean };

export async function joinOpenTogetherApi(giftId: string): Promise<OpenTogetherStatus | null> {
  const res = await apiFetch(`/api/gifts/${giftId}/open-together/join`, { method: "POST" });
  if (res.status === 401) {
    requestLock("unauthorized");
    return null;
  }
  if (!res.ok) return null;
  return (await res.json()) as OpenTogetherStatus;
}

export async function leaveOpenTogetherApi(giftId: string): Promise<void> {
  try {
    await apiFetch(`/api/gifts/${giftId}/open-together/leave`, { method: "POST" });
  } catch {
    // Best effort - a fire-and-forget cleanup call on unmount/navigate-away.
  }
}

export async function openTogetherStatusApi(giftId: string): Promise<OpenTogetherStatus | null> {
  const res = await apiFetch(`/api/gifts/${giftId}/open-together/status`);
  if (res.status === 401) {
    requestLock("unauthorized");
    return null;
  }
  if (!res.ok) return null;
  return (await res.json()) as OpenTogetherStatus;
}

export type CancelGiftResult = { ok: true } | { ok: false; error?: string };

export async function cancelGiftApi(giftId: string): Promise<CancelGiftResult> {
  const res = await apiFetch(`/api/gifts/${giftId}/cancel`, { method: "POST" });
  if (res.status === 401) {
    requestLock("unauthorized");
    return { ok: false };
  }
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    return { ok: false, error: data.message };
  }
  appState.gifts = appState.gifts.filter((g) => g.id !== giftId);
  notifyStateChange();
  return { ok: true };
}

export type EditGiftUnlockResult = { ok: true; gift: Gift } | { ok: false; error?: string };

export async function editGiftUnlockApi(giftId: string, unlockAt: string): Promise<EditGiftUnlockResult> {
  const res = await apiFetch(`/api/gifts/${giftId}/edit-unlock`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ unlockAt }),
  });
  if (res.status === 401) {
    requestLock("unauthorized");
    return { ok: false };
  }
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    return { ok: false, error: data.message };
  }
  const data = await res.json();
  appState.gifts = appState.gifts.map((g) => (g.id === giftId ? data.gift : g));
  notifyStateChange();
  return { ok: true, gift: data.gift };
}

// Pushed via the gift_cancelled WS event to the RECIPIENT when the sender
// cancels an unopened scheduled drop - removes it from the list the same
// way GiftsDB.getForUser() already filters it out server-side, so a refresh
// afterward looks identical to this live update.
export function applyGiftCancelled(data: { giftId: string }): void {
  appState.gifts = appState.gifts.filter((g) => g.id !== data.giftId);
  notifyStateChange();
}

// Pushed via the gift_edited WS event to the RECIPIENT when the sender
// changes an unopened scheduled drop's unlock time - full-replace-by-id,
// same shape as applyGiftUnlocked, so every surface reading the gift
// (list card, chat card, locked page) re-renders its countdown from the
// new unlockAt automatically.
export function applyGiftEdited(gift: Gift): void {
  appState.gifts = appState.gifts.map((g) => (g.id === gift.id ? gift : g));
  notifyStateChange();
}
